import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { PlusIcon, PencilIcon } from 'lucide-react';
import type { Language } from '@worldathletics/types';
import {
  ANNEX_PATH_PREFIX,
  parseAnnexHash,
  slugify,
  Permission,
} from '@worldathletics/utilities';

import { Button, Dropdown, type DropdownOptionProps } from '@/components/atoms';
import {
  PageContentLayout,
  PageContentLayoutHeader,
  PageContentLayoutMain,
  PageDefaultLayout,
} from '@/components/layout';
import {
  DocumentPreviewProviders,
  renderDocumentBlocks,
} from '@/components/document/document-preview';
import { AnnexLinkProvider } from '@/context/annex-link/annex-link-context';
import { CustomTitle } from '@/components/typography';
import { highlightFragmentInElement } from '@/lib/highlight-fragment';
import { consumeStashedRefTargetFragment } from '@/lib/ref-target-fragment-stash';
import { useAuth } from '@/hooks/useAuth';
import { usePermission } from '@/hooks/usePermission';
import { useAnnexAvailableLanguagesQuery } from '@/query/book/useAnnexAvailableLanguagesQuery';
import { useAnnexBookQuery } from '@/query/book/useAnnexBookQuery';
import { useAnnexPublishedNodeQuery } from '@/query/book/useAnnexPublishedNodeQuery';
import { useCreateDocumentQuery } from '@/query/book/useCreateDocumentQuery';
import type { Block } from '@worldathletics/types';

const LANGUAGE_LABELS: Record<string, string> = {
  EN: 'English',
  FR: 'French',
};

/** Walk the block tree and build a map from definition slug → block.id (disambiguated). */
function buildSlugToBlockIdMap(blocks: Block[]): Map<string, string> {
  const map = new Map<string, string>();
  const seenSlugs = new Map<string, number>();
  const walk = (nodes: Block[]): void => {
    for (const block of nodes) {
      if (
        block.type === 'container' &&
        block.style === 'definition' &&
        block.text
      ) {
        const base = slugify(block.text);
        const count = seenSlugs.get(base) ?? 0;
        const slug = count === 0 ? base : `${base}-${String(count + 1)}`;
        seenSlugs.set(base, count + 1);
        map.set(slug, block.id);
      }
      if (block.type === 'container') {
        walk(block.blocks);
      }
    }
  };
  walk(blocks);
  return map;
}

function scrollToAnnexTarget(
  blockId: string,
  targetFragment: string | null,
): void {
  const el = document.getElementById(blockId);
  if (!el) return;
  el.scrollIntoView({ behavior: 'auto', block: 'start' });
  el.classList.add('is-ref-target');
  window.setTimeout(() => el.classList.remove('is-ref-target'), 1500);
  if (targetFragment) {
    highlightFragmentInElement(el, targetFragment);
  }
}

export const Route = createFileRoute('/annex')({
  validateSearch: (search: Record<string, unknown>) => ({
    language: (search.language as Language) ?? ('EN' as Language),
  }),
  component: AnnexPage,
});

function AnnexPage() {
  const { isAuthenticated } = useAuth();
  const canCreate = usePermission(Permission.DocumentsCreate);
  const canEdit = usePermission(Permission.DocumentsEdit);
  const navigate = useNavigate();
  const { language } = useSearch({ from: '/annex' });

  const annexQuery = useAnnexBookQuery({ enabled: true });
  const bookId = annexQuery.data?.id;

  const nodeQuery = useAnnexPublishedNodeQuery(bookId, language ?? 'EN');
  const availableLanguagesQuery = useAnnexAvailableLanguagesQuery(bookId);
  const createDocument = useCreateDocumentQuery();
  const scrolledRef = useRef(false);

  useEffect(() => {
    if (!nodeQuery.data || scrolledRef.current) return;

    const hash = window.location.hash;
    const targetSlug = hash.startsWith('#') ? hash.slice(1) : null;
    const targetFragment = consumeStashedRefTargetFragment();

    if (!targetSlug) return;

    const parsed = parseAnnexHash(`${ANNEX_PATH_PREFIX}#${targetSlug}`);
    if (!parsed) return;

    const slugMap = buildSlugToBlockIdMap(nodeQuery.data.blocks);
    const parentBlockId = slugMap.get(parsed.parentSlug);

    let rafId = 0;
    let attempts = 0;
    const MAX_ATTEMPTS = 60;

    const tryScroll = () => {
      if (parsed.childBlockId) {
        const childEl = document.getElementById(parsed.childBlockId);
        if (childEl) {
          scrolledRef.current = true;
          scrollToAnnexTarget(parsed.childBlockId, targetFragment);
          return;
        }
      }

      if (parentBlockId) {
        const parentEl = document.getElementById(parentBlockId);
        if (parentEl) {
          scrolledRef.current = true;
          scrollToAnnexTarget(parentBlockId, targetFragment);
          return;
        }
      }

      if (++attempts < MAX_ATTEMPTS) {
        rafId = requestAnimationFrame(tryScroll);
      }
    };

    rafId = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(rafId);
  }, [nodeQuery.data]);

  const handleCreate = () => {
    const body = { name: 'Annex', kind: 'annex' } as unknown as Parameters<
      typeof createDocument.mutate
    >[0];
    createDocument.mutate(body, {
      onSuccess: created => {
        void navigate({
          to: '/document/$documentId',
          params: { documentId: created.id },
        });
      },
    });
  };

  const availableLanguages = availableLanguagesQuery.data ?? [];
  const languageOptions: DropdownOptionProps[] = availableLanguages.map(
    lang => ({ value: lang, label: LANGUAGE_LABELS[lang] ?? lang }),
  );
  const selectedLanguageOption =
    languageOptions.find(opt => opt.value === (language ?? 'EN')) ??
    languageOptions[0];

  const languageDropdown =
    languageOptions.length > 1 ? (
      <Dropdown
        options={languageOptions}
        value={selectedLanguageOption}
        onChange={(opt: DropdownOptionProps) => {
          void navigate({
            to: '/annex',
            search: { language: opt.value as Language },
          });
        }}
      />
    ) : null;

  const editControl =
    annexQuery.data ? (
      <div className="flex items-center gap-2">
        {languageDropdown}
        {canEdit && (
          <Button
            type="button"
            variant="default"
            onClick={() =>
              void navigate({
                to: '/document/$documentId',
                params: { documentId: annexQuery.data!.id },
              })
            }
          >
            <PencilIcon size={14} /> Edit annex
          </Button>
        )}
      </div>
    ) : (
      languageDropdown
    );

  const createCta =
    canCreate && annexQuery.isSuccess && !annexQuery.data ? (
      <div className="flex items-center justify-center py-20">
        <Button
          type="button"
          variant="default"
          onClick={handleCreate}
          disabled={createDocument.isPending}
        >
          <PlusIcon size={14} /> Create annex
        </Button>
      </div>
    ) : null;

  const isLoading = annexQuery.isLoading || nodeQuery.isLoading;
  const isError = annexQuery.isError || nodeQuery.isError;
  const node = nodeQuery.data;

  const content = (
    <PageContentLayout>
      <PageContentLayoutHeader buttons={editControl} className="">
        <CustomTitle level="h1">Annex</CustomTitle>
      </PageContentLayoutHeader>

      <PageContentLayoutMain>
        {isLoading && <p className="annex-page__loading">Loading...</p>}

        {isError && <p className="annex-page__error">Failed to load annex.</p>}

        {!isLoading &&
          !isError &&
          !node &&
          (createCta ?? (
            <p className="annex-page__empty">No published annex version yet.</p>
          ))}

        {node && (
          <AnnexLinkProvider>
            <DocumentPreviewProviders
              context={{
                tree: node.blocks,
                numberingStyle: node.numberingStyle,
                referencesMap: new Map(),
              }}
            >
              {renderDocumentBlocks(node.blocks, 0)}
            </DocumentPreviewProviders>
          </AnnexLinkProvider>
        )}
      </PageContentLayoutMain>
    </PageContentLayout>
  );

  if (isAuthenticated) {
    return <PageDefaultLayout>{content}</PageDefaultLayout>;
  }

  return content;
}
